import { useContext, useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import { Box, Button, Container } from '@mui/material';
import CenterFocusWeakIcon from '@mui/icons-material/CenterFocusWeak';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import { AppContext, SttTabs } from '../../../sporttia/all';
import DeviceDetails from './DeviceDetails';
import DeviceAuths from './DeviceAuths';
import DeviceAccesses from './DeviceAccesses';
import { SttFullDialog } from '../../../sporttia/dialogs/SttFullDialog';
import constants from '../../../config/constants';
import { PERMISSIONS } from '../../../lib/permissions';
import {
    LEGACY_PRIVILEGES,
    canOrHasPrivilege,
} from '../../../lib/permissionOrPrivilege';

type Device = {
    id?: string;
    name?: string;
    type?: string;
    description?: string;
    status?: string;
    signature?: string;
    preLight?: number;
    postLight?: number;
    preCortesy?: number;
    margin?: number;
    soundTime?: number;
    pendingPaymentsMargin?: number;
    antiPassback?: boolean;
    antiPassbackBonos?: boolean;
    checkDirectionOut?: boolean;
    remoteOpening?: boolean;
    facilities?: Array<{ id: string; name: string }>;
    trash?: boolean;
};

type Props = {
    scId: string;
    deviceId: string | null;
    onClose: () => void;
    initialTab?: number;
    onTabChange?: (tabIndex: number) => void;
};

export default function DeviceDetailsDialog({
    scId,
    deviceId,
    onClose,
    initialTab = 0,
    onTabChange,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [device, setDevice] = useState<Device | undefined>();
    const [simulateAccessOpen, setSimulateAccessOpen] =
        useState<boolean>(false);

    const isAdmin = cxt?.user?.role === 'ADMIN';
    // CU-8697paaj1: privilegio SC "Gestión de dispositivos" (id 29) para
    // editar. 869fed7ht: con los roles v8 encendidos, `device.modify`.
    const canEditDevice =
        isAdmin ||
        canOrHasPrivilege(
            cxt,
            PERMISSIONS.DEVICE.MODIFY,
            LEGACY_PRIVILEGES.MANAGE_DEVICES,
        );

    const setProperty = ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }): void => {
        if (!name) return;
        setDevice((prev) =>
            prev ? ({ ...prev, [name]: value } as Device) : prev,
        );
    };

    const loadDevice = (): void => {
        cxt?.api(
            'GET',
            `${constants.boundedContextPrefixes.MANAGER}/devices/${deviceId}`,
            {
                params: {
                    trash: true,
                },
                success: (r: { device: Device }) => {
                    setDevice(r.device);
                },
            },
        );
    };

    const saveDevice = (): void => {
        if (!device?.id || !canEditDevice) return;
        cxt?.api(
            'PUT',
            `${constants.boundedContextPrefixes.MANAGER}/devices/${device.id}`,
            {
                params: {
                    name: device.name,
                    type: device.type ? [device.type] : undefined,
                    description: device.description,
                    status: device.status,
                    signature: device.signature,
                    preLight: device.preLight,
                    postLight: device.postLight,
                    preCortesy: device.preCortesy,
                    margin: device.margin,
                    soundTime: device.soundTime,
                    pendingPaymentsMargin: device.pendingPaymentsMargin,
                    antiPassback: device.antiPassback,
                    antiPassbackBonos: device.antiPassbackBonos,
                    checkDirectionOut: device.checkDirectionOut,
                    remoteOpening: device.remoteOpening,
                },
                success: (r: { device: Device }) => {
                    setDevice(r.device);
                    cxt?.showMessage('S', t('Saved'));
                },
            },
        );
    };

    useEffect(() => {
        if (deviceId) loadDevice();
    }, [deviceId]);

    // Tabs
    const tabs = [
        {
            caption: t('Detail'),
            component: (
                <DeviceDetails
                    {...({
                        device,
                        scId: Number(scId),
                        onClose,
                        setProperty,
                        setSimulateAccessOpen,
                        simulateAccessOpen,
                    } as any)}
                />
            ),
        },
        {
            caption: t('Authorizations'),
            component: <DeviceAuths id={deviceId || undefined} />,
        },
        {
            caption: t('Accesses'),
            component: <DeviceAccesses id={deviceId || undefined} />,
            show: deviceId !== 'create',
        },
    ] as any;

    // -----| Render |-----
    return (
        <SttFullDialog
            open={deviceId !== null}
            title={deviceId === 'create' ? t('CreateDevice') : t('Device')}
            onClose={onClose}
            urlPath={
                deviceId && deviceId !== 'create'
                    ? `/devices/${deviceId}`
                    : undefined
            }
            bottomContent={
                <Box className="flex w-full justify-end" gap={1}>
                    <Button
                        variant="outlined"
                        startIcon={<CenterFocusWeakIcon />}
                        onClick={() => setSimulateAccessOpen(true)}
                    >
                        {t('SimulatePass')}
                    </Button>
                    {canEditDevice && device?.id && !device?.trash && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            onClick={saveDevice}
                        >
                            {t('Save')}
                        </Button>
                    )}
                </Box>
            }
        >
            <Container>
                <Box mt={2}>
                    <Paper>
                        <SttTabs
                            tabs={tabs}
                            initialTab={initialTab}
                            onTabChange={onTabChange}
                        />
                    </Paper>
                </Box>
            </Container>
        </SttFullDialog>
    );
}
